export type FetchParams = {
	name: string;
	url: string;
	headers?: Record<string, string | string[]>;
	options?: RequestInit;
};

/**
 * Get the hostname for the Pillar development server.
 *
 * In the always-on RDE, it will have a proper DNS name. In local development, it
 * will default to `localhost`.
 *
 * @returns The hostname for the development server.
 */
export const getDevHostname = (): string => {
	const envDns = process.env.RDE_DNS;
	return typeof envDns === 'string' && envDns.trim() ? envDns.trim() : 'localhost';
};

export function getProxyServerPort() {
	if (process.env.__PILLAR_PROXY_SERVER_PORT) {
		return parseInt(process.env.__PILLAR_PROXY_SERVER_PORT, 10);
	}

	return 9000;
}

/**
 * This function should return the URL for Pillar local development server.
 * @returns The URL for the Pillar development server.
 */
export function getLocalUrl() {
	return `${process.env.__PILLAR_ENABLE_HTTPS === 'true' ? 'https' : 'http'}://localhost:${getProxyServerPort()}`;
}

// FIXME: Pillar to define entire URL, not just hostname.
const LOCAL_URL = new URL(
	`${process.env.__PILLAR_ENABLE_HTTPS === 'true' ? 'https' : 'http'}://${getDevHostname()}:${getProxyServerPort()}`,
);

export function getReroutableURL(pathOrURL: string): string {
	// For local development environment only, otherwise removed during the bundling process
	if (process.env.PILLAR_LOCAL_DEVELOPMENT === 'true') {
		try {
			const u = new URL(pathOrURL, LOCAL_URL.href);
			u.host = LOCAL_URL.host;
			u.protocol = LOCAL_URL.protocol;
			return u.href;
		} catch (e) {
			// When the URL constructor has `base` set, and `pathOrUrl` is always a string,
			// no string can trigger this catch block.
			// Just in case, log the error to help with debugging.
			// eslint-disable-next-line no-console
			console.error('Failed to parse URL: ', pathOrURL, e);
		}
	}

	return pathOrURL;
}

export const fetchBase = async ({
	name,
	url,
	headers = {},
	options = {},
}: FetchParams): Promise<Response> => {
	const mergedOptions = { ...options, headers: { ...headers, ...options.headers } };

	try {
		const response = await fetch(getReroutableURL(url), mergedOptions);
		return response;
	} catch (err) {
		throw new Error(`Failed to fetch ${name} using ${url}`, { cause: err });
	}
};

export const fetchJson = async <T>({
	name,
	url,
	headers = {},
	options = {},
}: FetchParams): Promise<T> => {
	const response = await fetchBase({ name, url, headers, options });

	try {
		const data: T = await response.json();
		return data;
	} catch (err) {
		throw new Error(`Failed to parse the response of ${name} using ${url}`, { cause: err });
	}
};
export const fetchText = async ({
	name,
	url,
	headers = {},
	options,
}: FetchParams): Promise<string> => {
	const response = await fetchBase({ name, url, headers, options });

	const data: string = await response.text();

	return data;
};

declare class TesseractStaticCacheAPI {
	/**
	 * Load text from url and cache it, if it is not already cached
	 *
	 * @param url The url to load the text file from
	 */
	public loadText(url: string): Promise<string | undefined>;
	/**
	 * Load json object from url and cache it, if it is not already cached
	 * The json object will be deep freezed to prevent modification
	 *
	 * @param url - The url to load the json file from
	 */
	public loadJson<T>(url: string): Promise<T | undefined>;
}

declare global {
	var TesseractStaticCache: TesseractStaticCacheAPI;
}

export const fetchCachedText = async ({ name, url }: FetchParams) => {
	if (typeof globalThis.TesseractStaticCache === 'undefined') {
		throw new Error('Current runtime does not support TesseractStaticCache');
	}

	try {
		const text = await globalThis.TesseractStaticCache.loadText(url);
		return text;
	} catch (e) {
		throw new Error(`Failed to loadText for ${name} using ${url}`, { cause: e });
	}
};

export const fetchCachedJson = async <T>({ name, url }: FetchParams) => {
	if (typeof globalThis.TesseractStaticCache === 'undefined') {
		throw new Error('Current runtime does not support TesseractStaticCache');
	}

	try {
		const json = await globalThis.TesseractStaticCache.loadJson<T>(url);
		return json;
	} catch (e) {
		throw new Error(`Failed to loadJson for ${name} using ${url}`, { cause: e });
	}
};
